/**
 * useDatasetOverview — builds the advisor's terrain map, lazily.
 *
 * `enabled` is the drawer-open latch: nothing fires until the user first opens
 * the advisor (the build fans out ~a dozen metadata RPCs — free when react-query
 * has them warm, but not something app boot should pay for a panel that may
 * never open). A build that SUCCEEDS is session-stable by design (spec
 * §3.4-3.5) — the cached system prefix depends on that — so a reopen never
 * rebuilds once the overview has landed. A build that FAILS is the one
 * exception (FU-25): it re-arms on the next drawer reopen (the `enabled`
 * false→true flip), so a transient RPC outage doesn't strand the whole
 * session in loud v1 mode. A failure while the drawer stays open does not
 * retry on its own — only a reopen re-attempts.
 *
 * Failure is LOUD but non-blocking (spec §3.6): console.warn + failure message
 * for the unavailable marker; the advisor works view-scoped as v1.
 */

import { useEffect, useRef, useState } from 'react'
import type { ApiClient } from '../../api/apiClient'
import { buildDatasetOverview } from '../grounding/datasetOverview'
import type { DatasetOverview } from '../grounding/datasetOverview'

export interface OverviewHandle {
  overview: DatasetOverview | null
  failure: string | null
}

// idle: never attempted. started: in-flight OR succeeded (sticky — blocks re-fire).
// failed: last attempt rejected — the ONE state that lets the next enabled
// false→true flip re-arm (FU-25).
type BuildState = 'idle' | 'started' | 'failed'

export function useDatasetOverview(apiClient: ApiClient, enabled: boolean): OverviewHandle {
  const [handle, setHandle] = useState<OverviewHandle>({ overview: null, failure: null })
  const buildStateRef = useRef<BuildState>('idle')

  // True-unmount guard (T8 review fix). A per-effect `disposed` closure would
  // also flip on any dependency-change cleanup: open (build starts + latches)
  // → close (cleanup) → reopen (latch skips a rebuild) would orphan the
  // in-flight resolution and leave the overview null silently all session.
  // A resolution may land whenever this hook instance is still mounted.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true // StrictMode remount re-arms it
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled || buildStateRef.current === 'started') return
    buildStateRef.current = 'started'
    buildDatasetOverview(apiClient)
      .then((overview) => {
        // Sticky: buildStateRef stays 'started' — success never re-arms.
        if (mountedRef.current) setHandle({ overview, failure: null })
      })
      .catch((err: unknown) => {
        const failure = err instanceof Error ? err.message : String(err)
        console.warn('advisor: dataset overview build failed —', failure)
        buildStateRef.current = 'failed' // re-arms on next enabled false→true flip
        if (mountedRef.current) setHandle({ overview: null, failure })
      })
  }, [enabled, apiClient])

  return handle
}
